import { useEffect, useRef, useState } from 'react';

export const SCROLL_DISTANCE = 200;

export const useRadioClicksScroll = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  // Force re-render on window resize
  const [, forceUpdate] = useState({});

  const handleScrollLeft = () => {
    setScrollLeft(Math.max(0, scrollLeft - SCROLL_DISTANCE));
  };

  const handleScrollRight = () => {
    const maxScrollLeft = containerRef.current
      ? containerRef.current.scrollWidth - containerRef.current.clientWidth
      : 0;
    setScrollLeft(Math.min(maxScrollLeft, scrollLeft + SCROLL_DISTANCE));
  };

  // Add a function to scroll to the latest content (rightmost position)
  const scrollToLatest = () => {
    if (containerRef.current) {
      const maxScrollLeft =
        containerRef.current.scrollWidth - containerRef.current.clientWidth;
      setScrollLeft(maxScrollLeft);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      // Force recalculation of canScrollRight by triggering re-render
      forceUpdate({});
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Scroll to leftmost position on initial mount to see newest content
  useEffect(() => {
    if (containerRef.current) {
      const maxScrollLeft =
        containerRef.current.scrollWidth - containerRef.current.clientWidth;

      // If there's content that can be scrolled, scroll to the right
      if (maxScrollLeft > 0) {
        setScrollLeft(maxScrollLeft);
      }
    }
  }, [containerRef.current?.scrollWidth]);

  const canScrollLeft = scrollLeft > 0;
  const canScrollRight = containerRef.current
    ? scrollLeft <
      containerRef.current.scrollWidth - containerRef.current.clientWidth
    : false;

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Allow default scrolling behavior
    if (containerRef.current) {
      setScrollLeft(
        containerRef.current.scrollLeft + SCROLL_DISTANCE * e.deltaY,
      );
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [scrollLeft]);

  return {
    containerRef,
    handleScrollLeft,
    handleScrollRight,
    canScrollLeft,
    canScrollRight,
    handleWheel,
    scrollToLatest,
  };
};
